/**
 * Where a person is in the inbox (contract v10.25): the view, the folder, and the filters
 * on top, all of it in the address.
 *
 * The inbox opens on «Por revisar», the emails nobody has answered yet. The folders are
 * the mailbox's own, as «Correcto» files the mail there (ADR 0021 of the back), so the
 * rail says the same names Thunderbird does. Everything lives in the URL so «← Bandeja»
 * from an email, the browser's back and a pasted link all land on the same list, and the
 * detail knows which list «Anterior» and «Siguiente» walk.
 */
import { CATEGORIES, type EmailCategory } from '@/shared/triage/vocabulary';

import type { EmailListParams } from './api';
import type { InboxView } from './types';

export const PAGE_SIZE = 50;

/** The address keys, in Spanish like the routes. */
export const PARAM = {
  view: 'vista',
  folder: 'carpeta',
  mailbox: 'buzon',
  search: 'q',
  category: 'que',
  unread: 'sin_leer',
  page: 'pagina',
} as const;

/** Where the inbox opens: what is left to do. */
export const DEFAULT_VIEW: InboxView = 'por_revisar';

/** The views the rail lists by name. A folder is `carpeta` plus its name. */
export const VIEW_LABELS: Record<Exclude<InboxView, 'carpeta'>, string> = {
  por_revisar: 'Por revisar',
  todos: 'Todos',
  papelera: 'Papelera',
  spam: 'Spam del servidor',
  errores: 'Con error',
};

const VIEWS = ['por_revisar', 'carpeta', 'todos', 'papelera', 'spam', 'errores'] as const;

export interface InboxPlace {
  view: InboxView;
  /** Only with `view === 'carpeta'`: the folder's name as the panel has it. */
  folder: string | null;
}

/** `?vista=` and `?carpeta=` as a place; anything unknown is «Por revisar». */
export function placeFrom(params: URLSearchParams): InboxPlace {
  const folder = params.get(PARAM.folder)?.trim() || null;
  const raw = params.get(PARAM.view);
  if (folder) return { view: 'carpeta', folder };
  const view = (VIEWS as readonly string[]).includes(raw ?? '') ? (raw as InboxView) : null;
  if (!view || view === 'carpeta') return { view: DEFAULT_VIEW, folder: null };
  return { view, folder: null };
}

/** How the rail and the headings name a place. */
export function placeLabel(place: InboxPlace): string {
  if (place.view === 'carpeta') return place.folder ?? 'Carpeta';
  return VIEW_LABELS[place.view];
}

/** `?que=` as a category the contract knows, or '' for anything else. */
export function categoryFrom(value: string | null): EmailCategory | '' {
  return value && (CATEGORIES as readonly string[]).includes(value) ? (value as EmailCategory) : '';
}

/** `?pagina=` as an offset: page 1 (or anything that is not a page) is 0. */
export function offsetFrom(params: URLSearchParams): number {
  const page = Number.parseInt(params.get(PARAM.page) ?? '', 10);
  return Number.isFinite(page) && page > 1 ? (page - 1) * PAGE_SIZE : 0;
}

/**
 * What the list asks the API for, from the address: the place, the mailbox, the search,
 * «Qué es», «No leídos». No `limit` nor `offset`: the list adds its page, the neighbours
 * of a detail do not need one.
 */
export function listFilters(params: URLSearchParams): EmailListParams {
  const place = placeFrom(params);
  const mailbox = params.get(PARAM.mailbox) ?? '';
  const search = (params.get(PARAM.search) ?? '').trim();
  const category = categoryFrom(params.get(PARAM.category));
  return {
    view: place.view,
    ...(place.folder ? { folder: place.folder } : {}),
    ...(mailbox ? { mailbox } : {}),
    ...(search ? { search } : {}),
    ...(category ? { category } : {}),
    ...(params.get(PARAM.unread) === '1' ? { read: false } : {}),
  };
}

/** The address of a place, keeping the mailbox (a person reads one mailbox at a time)
 * and dropping what belonged to the list they leave: search, category, page. */
export function placeSearch(place: InboxPlace, current: URLSearchParams): string {
  const next = new URLSearchParams();
  if (place.view === 'carpeta' && place.folder) next.set(PARAM.folder, place.folder);
  else if (place.view !== DEFAULT_VIEW) next.set(PARAM.view, place.view);
  const mailbox = current.get(PARAM.mailbox);
  if (mailbox) next.set(PARAM.mailbox, mailbox);
  return next.toString();
}

const LIST_KEYS = Object.values(PARAM);

/** Only the list's own keys of an address, in a stable order, so a link from a row to
 * its email carries the list and nothing else. */
export function listSearch(params: URLSearchParams): string {
  const next = new URLSearchParams();
  for (const key of LIST_KEYS) {
    const value = params.get(key);
    if (value) next.set(key, value);
  }
  return next.toString();
}

/** `/correos` or `/correos?…`: the list a detail came from. */
export function inboxHref(params: URLSearchParams): string {
  const search = listSearch(params);
  return search ? `/correos?${search}` : '/correos';
}

/** `/correos/:id?…`: an email, remembering the list it was opened from. */
export function emailHref(id: string, params: URLSearchParams): string {
  const search = listSearch(params);
  return search ? `/correos/${id}?${search}` : `/correos/${id}`;
}

/**
 * The folder as a person reads it: `INBOX.Pedidos cliente` (Hostinet names every folder
 * under the inbox) is «Pedidos cliente». What the server calls it stays in the details.
 */
export function folderShortName(serverName: string): string {
  const cut = Math.max(serverName.lastIndexOf('.'), serverName.lastIndexOf('/'));
  return cut >= 0 ? serverName.slice(cut + 1) : serverName;
}
